import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { UserAvailabilityStatus } from '../../../constants'

export interface UserStatus {
  userId: number
  status: 'online' | 'offline' | string
  lastSeen: string | null | undefined
  // When the user was last online; null while online or when they hide it.
  lastActiveAt?: string | null
  // The user's main-platform hashkey, used by the website to look them up.
  hashkey?: string | null
}

export interface UserStatusss {
  status: UserAvailabilityStatus.Online | UserAvailabilityStatus.Offline | UserAvailabilityStatus.Away | string
  lastSeen?: string | null
}

interface UserStatusState {
  userStatuses: Record<number | string, UserStatus>
  // hashkey -> chat user ids. Usually one; an agent can have a duplicate chat
  // account, and the website picks the most active (utils/presence.ts pickPresence).
  byHashkey: Record<string, string[]>
  // Shared clock for getPresenceStatus, advanced once a minute so "Recently
  // active" turns into "Offline" without a server event.
  presenceNow: number
}

const initialState: UserStatusState = {
  userStatuses: {},
  byHashkey: {},
  presenceNow: Date.now(),
}

const upsertStatus = (state: UserStatusState, { userId, status, lastSeen, lastActiveAt, hashkey }: UserStatus) => {
  state.userStatuses[userId] = { userId, status, lastSeen, lastActiveAt, hashkey }
  if (!hashkey) return
  const id = String(userId)
  const ids = state.byHashkey[hashkey]
  if (!ids) state.byHashkey[hashkey] = [id]
  else if (!ids.includes(id)) ids.push(id)
}

const userStatusSlice = createSlice({
  name: 'userStatus',
  initialState,
  reducers: {
    updateUserStatus: (state, action: PayloadAction<UserStatus>) => {
      upsertStatus(state, action.payload)
    },
    updateBulkUserStatuses: (state, action: PayloadAction<UserStatus[]>) => {
      action.payload.forEach((userStatus) => upsertStatus(state, userStatus))
    },
    setPresenceNow: (state, action: PayloadAction<number>) => {
      state.presenceNow = action.payload
    },
    clearUserStatuses: (state) => {
      state.userStatuses = {}
      state.byHashkey = {}
    },
  },
})

export const { updateUserStatus, updateBulkUserStatuses, setPresenceNow, clearUserStatuses } = userStatusSlice.actions
export default userStatusSlice.reducer
